import { useState, FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button, Input, Card } from '../components';
import DarkModeToggle from '../components/DarkModeToggle';
import { useAuthStore } from '../lib/auth';

export default function LoginPage() {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState('');
  const { login, isLoading } = useAuthStore();
  const navigate = useNavigate();

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');

    try {
      const response = await login(email, password, rememberMe);

      // Check if device verification is required
      if (response?.requireDeviceVerification) {
        // Store email for device verification page
        sessionStorage.setItem('deviceVerificationEmail', email);
        navigate('/verify-device');
        return;
      }

      // Check if 2FA is required
      if (response?.requireTwoFactor) {
        // TODO: Navigate to 2FA challenge page
        setError(t('auth.login.twoFactorRequired'));
        return;
      }

      // Check if workspace needs onboarding
      const { workspace } = useAuthStore.getState();
      if (workspace && !workspace.onboardingCompleted) {
        navigate('/onboarding');
      } else if (workspace) {
        navigate(`/${workspace.slug}/dashboard`);
      } else {
        // Fallback if no workspace (shouldn't happen)
        navigate('/login');
      }
    } catch (err: any) {
      setError(
        err.response?.data?.message || t('auth.login.error')
      );
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-white dark:bg-gray-900 p-4 transition-colors duration-300">
      <Card className="w-full max-w-md relative">
        <DarkModeToggle />

        <div className="mb-8 text-center">
          <img
            src="/logo.png"
            alt={t('app.name')}
            className="h-24 mx-auto mb-6 dark:hidden"
          />
          <img
            src="/logo-white.png"
            alt={t('app.name')}
            className="h-24 mx-auto mb-6 hidden dark:block"
          />
          <h1 className="text-3xl font-bold mb-2 dark:text-gray-100">{t('auth.login.title')}</h1>
          <p className="text-lg text-gray-600 dark:text-gray-400">
            {t('app.tagline')}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <Input
            type="email"
            label={t('auth.login.email')}
            placeholder={t('auth.login.emailPlaceholder')}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            autoComplete="email"
            autoFocus
          />

          <div>
            <Input
              type="password"
              label={t('auth.login.password')}
              placeholder={t('auth.login.passwordPlaceholder')}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete="current-password"
            />
            <div className="mt-2 flex items-center justify-between">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  className="w-4 h-4 border-2 border-black dark:border-gray-300 rounded bg-white dark:bg-gray-800 checked:bg-black dark:checked:bg-white cursor-pointer focus:ring-2 focus:ring-black dark:focus:ring-white focus:ring-offset-2"
                />
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  {t('auth.login.rememberMe')}
                </span>
              </label>
              <a
                href="/forgot-password"
                className="text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-black dark:hover:text-white hover:underline"
              >
                {t('auth.login.forgotPassword')}
              </a>
            </div>
          </div>

          {error && (
            <div className="p-4 bg-red-50 dark:bg-red-900/20 border-2 border-red-500 rounded-lg">
              <p className="text-sm text-red-900 dark:text-red-200">{error}</p>
            </div>
          )}

          <Button
            type="submit"
            variant="primary"
            isLoading={isLoading}
            className="w-full"
          >
            {t('auth.login.submit')}
          </Button>
        </form>

        <div className="mt-6 space-y-6">
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t-2 border-gray-200 dark:border-gray-700" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-4 bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400">
                {t('auth.login.or')}
              </span>
            </div>
          </div>

          <div className="text-center">
            <a
              href="/magic-link"
              className="inline-flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-black dark:hover:text-white hover:underline"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-4 w-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                />
              </svg>
              {t('auth.login.magicLink')}
            </a>
          </div>

          <div className="pt-6 border-t-2 border-gray-200 dark:border-gray-700">
            <p className="text-sm text-gray-600 dark:text-gray-400 text-center">
              {t('auth.login.noAccount')}{' '}
              <a
                href="/register"
                className="font-semibold text-black dark:text-gray-100 hover:underline"
              >
                {t('auth.login.register')}
              </a>
            </p>
          </div>
        </div>
      </Card>
    </div>
  );
}
